/**
 * MapaCompradorV2Preview.jsx — Versão 2
 */
import { useRef, useState, Fragment } from 'react'
import { Printer } from 'lucide-react'
import api from '../../api/client'
import AlterarComissaoModal from '../../components/modals/AlterarComissaoModal'
import mapasService from '../../services/mapasService'
import IsentarGTAModal from '../../components/modals/IsentarGTAModal'
import { openPdf, downloadPdf } from '../../utils/pdfUtils'
import PrazoComissao from '../../components/mapas/PrazoComissao'
import { criasTxt, criasTotais } from './criasUtils'

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8001').replace(/\/$/, '')
const BLUE_HDR = '#1a3a5c'
const BLUE_TXT = '#1a5276'

const brl = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
const num = v => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0)
const fmtDate = s => { if (!s) return ''; try { const d = String(s).split('T')[0]; const [y,m,dd] = d.split('-'); return (y && m && dd) ? `${dd}/${m}/${y}` : new Date(s).toLocaleDateString('pt-BR') } catch { return s } }

const S = {
  page:     { fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 14, color: '#000', background: '#fff', padding: '16px 24px 20px' },
  hdr:      { display: 'flex', alignItems: 'center', gap: 20, marginBottom: 6, paddingBottom: 6 },
  hdrLogo:  { maxHeight: 64, maxWidth: 160, objectFit: 'contain' },
  hdrT1:    { fontSize: 18, fontWeight: 700, color: BLUE_TXT },
  hdrT2:    { fontSize: 14, color: '#333', marginTop: 3, fontWeight: 700 },
  hdrT3:    { fontSize: 13, color: '#666', marginTop: 1 },
  compNome: { fontSize: 20, fontWeight: 700, textAlign: 'center', marginBottom: 2, padding: '1.5px 0', border: '1.5px solid #333' },
  compGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, fontSize: 13, marginBottom: 12, padding: '8px 0' },
  compLbl:  { color: BLUE_TXT, fontWeight: 700 },
  secTitle: { fontSize: 13, fontWeight: 700, color: '#000000', marginBottom: 3, marginTop: 12, textAlign: 'center', textTransform: 'uppercase' },
  tbl:      { width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 8 },
  thBlue:   { background: '#f0f0f0', color: '#000', padding: '6px 8px', textAlign: 'center', fontWeight: 700, fontSize: 12, border: '1px solid #ddd' },
  td:       { border: '1px solid #ddd', padding: '5px 8px', verticalAlign: 'top' },
  tdC:      { border: '1px solid #ddd', padding: '5px 8px', textAlign: 'center', verticalAlign: 'top' },
  tdR:      { border: '1px solid #ddd', padding: '5px 8px', textAlign: 'right', verticalAlign: 'top' },
  resumo:   { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0, border: '1px solid #ddd', marginTop: 10, marginBottom: 12 },
  resumoCol:{ padding: '10px 14px', borderRight: '1px solid #ddd', fontSize: 13, lineHeight: 1.6, textAlign: 'center' },
  resumoLbl:{ color: BLUE_TXT, fontWeight: 700 },
  footer:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 16, paddingTop: 10, borderTop: '1px solid #ccc', fontSize: 13 },
  footerSite:{ fontSize: 13, fontWeight: 700, color: BLUE_TXT, marginTop: 8 },
}

function Sec({ title, children }) {
  return <div>
    {title && <div style={S.secTitle}>{title}</div>}
    {children}
  </div>
}

const DEFAULT_COLUNAS_V2_COMP = ['lote', 'curral', 'vendedor', 'situacao', 'qtd', 'descricao', 'valor_unitario', 'total', 'liquido', 'comissao']

function MapaPageV2({ comp, leilao, organizador, showDbVendedor, colunasAtivas = DEFAULT_COLUNAS_V2_COMP, destaqueTotalCompradores = 'liquido', ocultarTotalLiquido = false }) {
  const mostrarLote        = colunasAtivas.includes('lote')
  const mostrarCurral      = colunasAtivas.includes('curral')
  const mostrarVendedor    = colunasAtivas.includes('vendedor')
  const mostrarSituacao    = colunasAtivas.includes('situacao')
  const mostrarQtd         = colunasAtivas.includes('qtd')
  const mostrarDescricao   = colunasAtivas.includes('descricao')
  const mostrarValorUnit   = colunasAtivas.includes('valor_unitario')
  const mostrarTotal       = colunasAtivas.includes('total')
  const mostrarLiquido     = colunasAtivas.includes('liquido')
  const mostrarComissao    = colunasAtivas.includes('comissao')
  const mostrarPct         = colunasAtivas.includes('pct')
  const lotes = comp.lotes || []
  const criasTot = criasTotais(lotes)

  let totalVv = 0, totalCv = 0, totalCc = 0, totalDesp = 0, totalDesconto = 0
  let totalDefVv = 0, totalDefCv = 0
  let qtdCompras = 0, qtdDefesas = 0, qtdAnimais = 0, qtdAnimaisD = 0
  let totalFemea = 0, qtdFemea = 0, totalMacho = 0, qtdMacho = 0, totalMisto = 0, qtdMisto = 0
  let totalLiqAll = 0

  lotes.forEach(l => {
    const vv = l.vv || 0; const qtd = l.qtd || 0
    if (l.situacao === 'VENDA') {
      totalVv += vv; totalCv += l.cv_val; totalCc += l.cc_val
      totalDesp += l.desp; totalDesconto += (l.desconto || 0); qtdCompras++; qtdAnimais += qtd
    } else { totalDefVv += vv; totalDefCv += l.cv_val; qtdDefesas++; qtdAnimaisD += qtd }
    const sexo = (l.sexo || '').toUpperCase()
    if (sexo === 'FEMEA' || sexo === 'FÊMEA') { totalFemea += vv; qtdFemea += qtd }
    else if (sexo === 'MACHO') { totalMacho += vv; qtdMacho += qtd }
    else { totalMisto += vv; qtdMisto += qtd }
  })

  const fluxoLeilao = leilao.fluxo_comissao || ''
  const despSomavel = fluxoLeilao === 'separada' ? 0 : totalDesp
  const totalGeral  = totalVv + totalDefVv
  const totalComiss = totalCc + totalCv + totalDefCv + despSomavel

  // ── Cálculo GTA + Nota (fluxo gta_nota) ──────────────────────────────────
  // Isenção POR LOTE e INDEPENDENTE por componente (gta_isento / nota_isento),
  // igual ao backend (reports.base.calcular_gta_nota).
  const gtaIsento = !!comp.gta_isento && !!comp.nota_isento  // tudo isento → esconde bloco
  let totalGtaNota = 0
  let gtaTotal = 0
  let notaTotal = 0

  if (fluxoLeilao === 'gta_nota' && !gtaIsento) {
    const valorPorAnimal = parseFloat(leilao.valor_por_animal) || 0
    const valorNota = parseFloat(leilao.valor_nota) || 0
    const gtaModo = leilao.gta_modo_cobranca || 'por_vendedor'

    if (valorPorAnimal > 0 || valorNota > 0) {
      const vendaLotes = lotes.filter(l => l.situacao === 'VENDA')
      const lotesGta = vendaLotes.filter(l => !l.gta_isento)
      const lotesNota = vendaLotes.filter(l => !l.nota_isento)
      gtaTotal = valorPorAnimal * lotesGta.reduce((s, l) => s + (l.qtd || 0), 0)

      if (gtaModo === 'por_lote') {
        notaTotal = valorNota * lotesNota.length
      } else {
        const vendedoresSet = new Set()
        lotesNota.forEach(l => { if (l.vendedor_id) vendedoresSet.add(l.vendedor_id) })
        notaTotal = valorNota * vendedoresSet.size
      }
      totalGtaNota = gtaTotal + notaTotal
    }
  }

  const totalComCc  = totalGeral + totalCc + despSomavel + totalGtaNota - totalDesconto

  lotes.forEach(l => {
    const liq = fluxoLeilao === 'separada'
      ? (l.vv || 0) + (l.cc_val || 0)
      : (l.parcelas || []).reduce((s, p) => s + (p.liquido || 0), 0)
    totalLiqAll += liq
  })

  // Médias consideram comissão de compra + GTA/Nota
  const mediaFemea  = qtdFemea ? (totalFemea + (qtdFemea / qtdAnimais) * (totalCc + totalGtaNota)) / qtdFemea : 0
  const mediaMacho  = qtdMacho ? (totalMacho + (qtdMacho / qtdAnimais) * (totalCc + totalGtaNota)) / qtdMacho : 0
  const mediaMisto  = qtdMisto ? (totalMisto + (qtdMisto / qtdAnimais) * (totalCc + totalGtaNota)) / qtdMisto : 0
  const mediaGeral  = qtdAnimais > 0 ? (totalVv + totalCc + totalGtaNota) / qtdAnimais : 0

  // Agrupa parcelas por vendedor — exclui DEFESA onde o vendedor é o próprio comprador
  const vendedoresMap = {}
  lotes.forEach(l => {
    if (l.situacao === 'DEFESA' && l.vendedor_id === comp.id) return
    const key = l.vendedor_nome || '-'
    if (!vendedoresMap[key]) {
      vendedoresMap[key] = { nome: key, cpf: l.vendedor_cpf || '', lotes: [], parcelas: [], totalParcelas: 0, db: null }
      if (showDbVendedor) {
        const dbs = l.vendedor_dados_bancarios || []
        const db  = dbs.find(d => d.is_principal) || dbs[0] || null
        vendedoresMap[key].db = db
      }
    }
    vendedoresMap[key].lotes.push(l.numero_lote)
    ;(l.parcelas || []).forEach(p => {
      vendedoresMap[key].parcelas.push({ ...p, numero_lote: l.numero_lote })
      vendedoresMap[key].totalParcelas += p.liquido || 0
    })
  })

  // Ordena parcelas de cada vendedor por data e agrupa por data de vencimento
  const normalizeDate = (d) => {
    if (!d) return ''
    const s = String(d)
    if (s.includes('T')) return s.split('T')[0]
    return s.slice(0, 10)
  }
  Object.values(vendedoresMap).forEach(v => {
    v.parcelas.sort((a, b) => {
      if (!a.data) return 1
      if (!b.data) return -1
      return new Date(a.data) - new Date(b.data)
    })
    // Agrupa parcelas com mesma data
    const grouped = {}
    v.parcelas.forEach(p => {
      const key = normalizeDate(p.data)
      if (grouped[key]) {
        grouped[key].liquido += p.liquido || 0
        if (p.numero_lote && !grouped[key].lotes_parcela.includes(p.numero_lote)) {
          grouped[key].lotes_parcela.push(p.numero_lote)
        }
      } else {
        grouped[key] = { ...p, data: normalizeDate(p.data), lotes_parcela: [p.numero_lote].filter(Boolean) }
      }
    })
    v.parcelas = Object.values(grouped).sort((a, b) => (a.data || '').localeCompare(b.data || ''))
    v.totalParcelas = v.parcelas.reduce((s, p) => s + (p.liquido || 0), 0)
  })

  const orgDb = organizador.dados_bancarios
  const logoSrc = organizador.logo_url
    ? (organizador.logo_url.startsWith('http') ? organizador.logo_url
        : `${API_BASE}${organizador.logo_url.startsWith('/') ? '' : '/'}${organizador.logo_url}`)
    : null

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.hdr}>
        <div style={{ width: 160 }}>{logoSrc ? <img src={logoSrc} alt="logo" style={S.hdrLogo} /> : <span style={{ fontWeight: 700, fontSize: 16, color: BLUE_TXT }}>{organizador.nome}</span>}</div>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={S.hdrT1}>Mapa do Comprador</div>
          <div style={S.hdrT2}>{leilao.nome} - {fmtDate(leilao.data_leilao)}</div>
          <div style={S.hdrT3}>{organizador.nome}</div>
        </div>
        <div style={{ width: 160, textAlign: 'right' }}>
          {leilao.banner_url && (
            <img
              src={leilao.banner_url.startsWith('http') ? leilao.banner_url : `${API_BASE}${leilao.banner_url.startsWith('/') ? '' : '/'}${leilao.banner_url}`}
              alt="banner leilão"
              style={{ maxHeight: 64, maxWidth: 160, objectFit: 'contain', marginLeft: 'auto' }}
            />
          )}
        </div>
      </div>

      {/* Nome comprador */}
      <div style={{ ...S.compNome, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
        <span>{comp.nome.toUpperCase()}</span>
        {(comp.gta_isento || comp.nota_isento) && (
          <span style={{ background: '#dcfce7', color: '#166534', border: '1px solid #86efac', borderRadius: 4, fontSize: 10, fontWeight: 700, padding: '1px 7px', letterSpacing: 0.3 }}>
            {comp.gta_isento && comp.nota_isento ? 'GTA/NOTA ISENTOS' : comp.gta_isento ? 'GTA ISENTA' : 'NOTA ISENTA'}
          </span>
        )}
      </div>

      {/* Dados comprador — 3 colunas */}
      <div style={S.compGrid}>
        <div>
          <div><span style={S.compLbl}>CPF/CNPJ:</span> {comp.cpf_cnpj || '—'}</div>
          <div><span style={S.compLbl}>Endereço:</span> {comp.endereco || '—'}</div>
          <div><span style={S.compLbl}>Bairro:</span> {comp.bairro || '—'}</div>
          <div><span style={S.compLbl}>Município:</span> {comp.cidade ? `${comp.cidade} - ${comp.uf || ''}` : '—'}</div>
        </div>
        <div>
          <div><span style={S.compLbl}>CEP:</span> {comp.cep || '—'}</div>
          <div><span style={S.compLbl}>Telefone:</span> {comp.telefone || '—'}</div>
          <div><span style={S.compLbl}>Email:</span> {comp.email || '—'}</div>
        </div>
        <div>
          <div><span style={S.compLbl}>Fazenda:</span> {comp.fazenda || '—'}</div>
          <div><span style={S.compLbl}>I.E.:</span> {comp.fazenda_ie || '—'}</div>
          <div><span style={S.compLbl}>Cidade/UF:</span> {comp.fazenda_cidade ? `${comp.fazenda_cidade} - ${comp.fazenda_uf || ''}` : '—'}</div>
        </div>
      </div>

      {/* Lotes */}
        <table style={{ ...S.tbl, tableLayout: 'fixed' }}>
          <colgroup>
            {mostrarLote && <col style={{ width: '5%' }} />}
            {mostrarCurral && <col style={{ width: '6%' }} />}
            {mostrarVendedor && <col style={{ width: mostrarCurral ? '17%' : '21%' }} />}
            {mostrarSituacao && <col style={{ width: '5%' }} />}
            {mostrarQtd && <col style={{ width: '4%' }} />}
            {mostrarDescricao && <col style={{ width: mostrarPct ? '20%' : '23%' }} />}
            {mostrarValorUnit && <col style={{ width: '9%' }} />}
            {mostrarTotal && <col style={{ width: '9%' }} />}
            {mostrarLiquido && <col style={{ width: '9%' }} />}
            {mostrarComissao && <col style={{ width: mostrarPct ? '10%' : '12%' }} />}
            {mostrarPct && <col style={{ width: '6%' }} />}
          </colgroup>
          <thead>
            <tr>
              {mostrarLote && <th style={S.thBlue}>Lote</th>}
              {mostrarCurral && <th style={S.thBlue}>Curral</th>}
              {mostrarVendedor && <th style={S.thBlue}>Vendedor</th>}
              {mostrarSituacao && <th style={S.thBlue}>Sit</th>}
              {mostrarQtd && <th style={S.thBlue}>Qt</th>}
              {mostrarDescricao && <th style={S.thBlue}>Descrição</th>}
              {mostrarValorUnit && <th style={S.thBlue}>Unitário</th>}
              {mostrarTotal && <th style={S.thBlue}>Total</th>}
              {mostrarLiquido && <th style={S.thBlue}>{fluxoLeilao === 'separada' ? 'Total + Com.' : 'Líquido'}</th>}
              {mostrarComissao && <th style={S.thBlue}>Comissões</th>}
              {mostrarPct && <th style={S.thBlue}>%</th>}
            </tr>
          </thead>
          <tbody>
            {lotes.map((l, i) => {
              const pesoStr = l.peso ? `${Number(l.peso).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}kg` : ''
              const descLine1 = [l.tipo_animal, l.raca].filter(Boolean).join(' ').toUpperCase()
              const descLine2 = [l.idade, l.sexo, pesoStr].filter(Boolean).join(' ').toUpperCase()
              const desc = [descLine1, descLine2].filter(Boolean).join('\n')
              const liq  = fluxoLeilao === 'separada'
                ? (l.vv || 0) + (l.cc_val || 0)
                : (l.parcelas || []).reduce((s, p) => s + (p.liquido || 0), 0)
              return (
                <Fragment key={i}>
                <tr style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                  {mostrarLote && <td style={{ ...S.tdC, fontWeight: 700 }}>{l.numero_lote}</td>}
                  {mostrarCurral && <td style={S.tdC}>{l.curral || '—'}</td>}
                  {mostrarVendedor && <td style={{ ...S.td, wordBreak: 'break-word' }}>{l.vendedor_nome}</td>}
                  {mostrarSituacao && <td style={{ ...S.tdC, whiteSpace: 'nowrap' }}>{l.situacao === 'VENDA' ? 'VEN' : l.situacao === 'DEFESA' ? 'DEF' : l.situacao}</td>}
                  {mostrarQtd && <td style={S.tdC}>{l.qtd}</td>}
                  {mostrarDescricao && <td style={{ ...S.tdC, whiteSpace: 'pre-line' }}>{desc}</td>}
                  {mostrarValorUnit && <td style={{ ...S.tdR, whiteSpace: 'nowrap' }}>{num(l.vv_unit)}</td>}
                  {mostrarTotal && <td style={{ ...S.tdR, whiteSpace: 'nowrap' }}>{num(l.vv)}</td>}
                  {mostrarLiquido && <td style={{ ...S.tdR, whiteSpace: 'nowrap' }}>{num(liq)}</td>}
                  {mostrarComissao && <td style={{ ...S.td, fontSize: 11, whiteSpace: 'nowrap' }}>
                    C: {brl(l.cc_val)}{fluxoLeilao !== 'separada' && <><br />V: {brl(l.cv_val)}</>}
                  </td>}
                  {mostrarPct && <td style={{ ...S.td, fontSize: 11, whiteSpace: 'nowrap' }}>C: {(l.cc_pct || 0).toFixed(2)}%</td>}
                </tr>
                {criasTxt(l) && (
                  <tr style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td colSpan={[mostrarLote, mostrarCurral, mostrarVendedor, mostrarSituacao, mostrarQtd, mostrarDescricao, mostrarValorUnit, mostrarTotal, mostrarLiquido, mostrarComissao, mostrarPct].filter(Boolean).length} style={{ ...S.td, fontSize: 10, padding: '1px 6px' }}>
                      <strong>Crias:</strong> {criasTxt(l)}
                    </td>
                  </tr>
                )}
                </Fragment>
              )
            })}
          </tbody>
          <tfoot>
            <tr style={{ background: '#f0f0f0', fontWeight: 700, fontSize: 12 }}>
              <td
                colSpan={[mostrarLote, mostrarCurral, mostrarVendedor, mostrarSituacao, mostrarQtd, mostrarDescricao].filter(Boolean).length}
                style={{ border: '1px solid #ddd', padding: '6px 10px' }}
              >
                <span style={{ marginRight: 24 }}>Total de Animais: {qtdAnimais + qtdAnimaisD}</span>
                <span style={{ marginRight: 24 }}>Total de Fêmeas: {qtdFemea}</span>
                <span style={{ marginRight: criasTot.txt ? 24 : 0 }}>Total de Machos: {qtdMacho}</span>
                {criasTot.txt && <span>Crias: {criasTot.txt}</span>}
              </td>
              {mostrarValorUnit && <td style={{ border: '1px solid #ddd', padding: '6px 10px' }} />}
              {mostrarTotal && <td style={{ border: '1px solid #ddd', padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>{num(totalGeral)}</td>}
              {mostrarLiquido && <td style={{ border: '1px solid #ddd', padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>{num(totalLiqAll)}</td>}
              {mostrarComissao && <td style={{ border: '1px solid #ddd', padding: '6px 10px' }} />}
              {mostrarPct && <td style={{ border: '1px solid #ddd', padding: '6px 10px' }} />}
            </tr>
          </tfoot>
        </table>

      {/* Resumo — 3 colunas */}
        <div style={S.resumo}>
          <div style={S.resumoCol}>
            <div><span style={S.resumoLbl}>Compras:</span> Lotes: {qtdCompras}, Qtde: {qtdAnimais}</div>
            <div><span style={S.resumoLbl}>Defesas:</span> Lotes: {qtdDefesas}, Qtde: {qtdAnimaisD}</div>
            <div><span style={S.resumoLbl}>Quantidade de Animais:</span> {qtdAnimais + qtdAnimaisD}</div>
            {criasTot.txt && <div><span style={S.resumoLbl}>Crias:</span> {criasTot.txt}</div>}
            {fluxoLeilao !== 'separada' && <div><span style={S.resumoLbl}>Despesas:</span> {brl(totalDesp)}</div>}
          </div>
          <div style={S.resumoCol}>
            <div><span style={S.resumoLbl}>Comissão da Compra:</span> {brl(totalCc)}</div>
            {fluxoLeilao !== 'separada' && <div><span style={S.resumoLbl}>Comissão Venda:</span> {brl(totalCv)}</div>}
            {fluxoLeilao !== 'separada' && <div><span style={S.resumoLbl}>Comissão Defesa:</span> {brl(totalDefCv)}</div>}
            {fluxoLeilao === 'gta_nota' && totalGtaNota > 0 && (
              <div><span style={S.resumoLbl}>(GTA + NOTA):</span> {brl(totalGtaNota)}</div>
            )}
          </div>
          <div style={{ ...S.resumoCol, borderRight: 'none' }}>
            <div><span style={S.resumoLbl}>Média Fêmea:</span> {brl(mediaFemea)}</div>
            <div><span style={S.resumoLbl}>Média Macho:</span> {brl(mediaMacho)}</div>
            {mediaMisto > 0 && <div><span style={S.resumoLbl}>Média Misto:</span> {brl(mediaMisto)}</div>}
            <div><span style={S.resumoLbl}>Média Geral:</span> {brl(mediaGeral)}</div>
          </div>
        </div>

      {/* Pagamentos Vendedores */}
      <Sec title="Pagamentos Vendedores">
        <table style={{ ...S.tbl, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: showDbVendedor ? '28%' : '35%' }} />
            <col style={{ width: showDbVendedor ? '8%' : '10%' }} />
            <col style={{ width: showDbVendedor ? '22%' : '27%' }} />
            <col style={{ width: showDbVendedor ? '12%' : '28%' }} />
            {showDbVendedor && <col style={{ width: '20%' }} />}
          </colgroup>
          <thead>
            <tr>
              <th style={S.thBlue}>Vendedor</th>
              <th style={S.thBlue}>Lotes</th>
              <th style={S.thBlue}>Pagamento</th>
              <th style={S.thBlue}>Prazo</th>
              {showDbVendedor && <th style={S.thBlue}>Dados Bancários</th>}
            </tr>
          </thead>
          <tbody>
            {Object.values(vendedoresMap).map((v, vi) => {
              const parcCount = v.parcelas.length || 1
              return v.parcelas.map((p, pi) => (
                <tr key={`${vi}-${pi}`} style={{ background: vi % 2 === 0 ? '#fff' : '#fafafa' }}>
                  {pi === 0 && (
                    <>
                      <td style={{ ...S.tdC, verticalAlign: 'middle', borderBottom: '1px solid #ddd' }} rowSpan={parcCount}>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{v.nome}</div>
                        {v.cpf && <div style={{ fontSize: 12 }}>CPF/CNPJ: {v.cpf}</div>}
                        <div style={{ fontSize: 12 }}>Total de parcelas: {parcCount}</div>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>Valor total: {brl(v.totalParcelas)}</div>
                      </td>
                    </>
                  )}
                  <td style={{ ...S.tdC, borderBottom: '1px solid #eee' }}>{(p.lotes_parcela || []).join(', ')}</td>
                  <td style={{ ...S.tdC, borderBottom: '1px solid #eee' }}>
                    <div style={{ fontWeight: 700 }}>{brl(p.liquido)}</div>
                    <div style={{ fontSize: 11 }}>Venc: {fmtDate(p.data)}</div>
                  </td>
                  <td style={{ ...S.tdC, borderBottom: '1px solid #eee' }}>{p.prazo}</td>
                  {pi === 0 && showDbVendedor && (
                    <td style={{ ...S.tdC, verticalAlign: 'middle', fontSize: 12, borderBottom: '1px solid #ddd' }} rowSpan={parcCount}>
                      {v.db ? (
                        <div>
                          {v.db.banco   && <div>Banco: {v.db.banco}</div>}
                          {v.db.pix     && <div style={{ wordBreak: 'break-all' }}>PIX: {v.db.pix}</div>}
                          {v.db.agencia && <div>AG: {v.db.agencia} C/C: {v.db.conta}</div>}
                          {v.db.is_terceiro && v.db.titular && <div>Titular: {v.db.titular}</div>}
                          {v.db.cpf_cnpj_titular && <div>CPF/CNPJ: {v.db.cpf_cnpj_titular}</div>}
                          {v.db.observacoes && <div style={{ fontStyle: 'italic' }}>OBS: {v.db.observacoes}</div>}
                        </div>
                      ) : '—'}
                    </td>
                  )}
                </tr>
              ))
            })}
          </tbody>
        </table>
      </Sec>

      {/* Pagamentos Comissões */}
      <Sec title="Pagamentos Comissões">
        <table style={S.tbl}>
          <thead>
            <tr>{['Organizador','Valor','Dados Bancários'].map(h => <th key={h} style={S.thBlue}>{h}</th>)}</tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ ...S.tdC, whiteSpace: 'nowrap' }}>
                <div style={{ fontWeight: 700 }}>{organizador.nome}</div>
                {organizador.cnpj && <div style={{ fontSize: 12 }}>CNPJ: {organizador.cnpj}</div>}
                {(organizador.cidade || organizador.uf) && (
                  <div style={{ fontSize: 12 }}>{[organizador.cidade, organizador.uf].filter(Boolean).join(' - ')}</div>
                )}
              </td>
              <td style={{ ...S.tdC, fontWeight: 700, whiteSpace: 'nowrap', fontSize: 16, color: BLUE_TXT }}><strong>{brl(fluxoLeilao === 'separada' ? totalCc : totalComiss + totalGtaNota)}</strong></td>
              <td style={{ ...S.tdC, whiteSpace: 'nowrap' }}>
                {orgDb ? (
                  <div style={{ fontSize: 12 }}>
                    {orgDb.banco   && <div>Banco: {orgDb.banco}</div>}
                    {orgDb.pix     && <div>PIX: {orgDb.pix}</div>}
                    {orgDb.agencia && <div>AG: {orgDb.agencia} C/C: {orgDb.conta}</div>}
                  </div>
                ) : '—'}
              </td>
            </tr>
          </tbody>
        </table>
      </Sec>

      {/* TOTAL GERAL */}
      <div style={{ border: '1px solid #999', marginTop: 12, marginBottom: 8 }}>
        <div style={{ background: '#f0f0f0', borderBottom: '1px solid #999', padding: '2px 0', textAlign: 'center', fontWeight: 700, fontSize: 13 }}>
          TOTAL GERAL
        </div>
        <div style={{ display: 'flex' }}>
          <div style={{ flex: 1, padding: '8px 12px', textAlign: 'center', borderRight: '1px solid #ddd' }}>
            <div style={{ fontSize: 10, color: '#555', marginBottom: 2 }}>Total Animais</div>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{qtdAnimais + qtdAnimaisD}</div>
            {criasTot.txt && <div style={{ fontSize: 10, color: '#555' }}>Crias: {criasTot.txt}</div>}
          </div>
          <div style={{ flex: 1, padding: '8px 12px', textAlign: 'center', borderRight: '1px solid #ddd' }}>
            <div style={{ fontSize: 10, color: '#555', marginBottom: 2 }}>Total Compra</div>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{brl(totalGeral)}</div>
          </div>
          <div style={{ flex: 1, padding: '8px 12px', textAlign: 'center', borderRight: '1px solid #ddd' }}>
            <div style={{ fontSize: 10, color: '#555', marginBottom: 2 }}>
              {fluxoLeilao === 'gta_nota' && totalGtaNota > 0 ? 'Com./Taxa Geral' : 'Total Comissões'}
            </div>
            <div style={{ fontWeight: 700, fontSize: 13 }}>
              {brl(fluxoLeilao === 'separada' ? totalCc : fluxoLeilao === 'gta_nota' && totalGtaNota > 0 ? totalComiss + totalGtaNota : totalComiss)}
            </div>
          </div>
          {fluxoLeilao === 'separada' ? (
            <div style={{ flex: 1, padding: '8px 12px', textAlign: 'center', background: '#1a3a5c', color: '#fff' }}>
              <div style={{ fontSize: 10, marginBottom: 2, opacity: 0.85 }}>
                {fluxoLeilao === 'gta_nota' && totalGtaNota > 0 ? 'Gado + Com. + Taxas' : 'Total + Comissões'}
              </div>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{brl(totalComCc)}</div>
            </div>
          ) : destaqueTotalCompradores === 'total_mais_comissoes' ? (
            <>
              {!ocultarTotalLiquido && (
                <div style={{ flex: 1, padding: '8px 12px', textAlign: 'center', borderRight: '1px solid #ddd' }}>
                  <div style={{ fontSize: 10, color: '#555', marginBottom: 2 }}>Total Líquido</div>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{brl(totalLiqAll)}</div>
                </div>
              )}
              <div style={{ flex: 1, padding: '8px 12px', textAlign: 'center', background: '#1a3a5c', color: '#fff' }}>
                <div style={{ fontSize: 10, marginBottom: 2, opacity: 0.85 }}>
                  {fluxoLeilao === 'gta_nota' && totalGtaNota > 0 ? 'Gado + Com. + Taxas' : 'Total + Comissões'}
                </div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{brl(totalComCc)}</div>
              </div>
            </>
          ) : (
            <>
              <div style={{ flex: 1, padding: '8px 12px', textAlign: 'center', ...(!ocultarTotalLiquido ? { borderRight: '1px solid #ddd' } : {}) }}>
                <div style={{ fontSize: 10, color: '#555', marginBottom: 2 }}>
                  {fluxoLeilao === 'gta_nota' && totalGtaNota > 0 ? 'Gado + Com. + Taxas' : 'Total + Comissões'}
                </div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{brl(totalComCc)}</div>
              </div>
              {!ocultarTotalLiquido && (
                <div style={{ flex: 1, padding: '8px 12px', textAlign: 'center', background: '#1a3a5c', color: '#fff' }}>
                  <div style={{ fontSize: 10, marginBottom: 2, opacity: 0.85 }}>Total Líquido</div>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{brl(totalLiqAll)}</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Rodapé */}
      <div style={{ ...S.footer, flexDirection: 'column', alignItems: 'center', gap: 8, marginTop: 30 }}>
        <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }} />
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ borderTop: '1px solid #000', width: 220, margin: '0 auto', paddingTop: 4 }}>{comp.nome.toUpperCase()}</div>
          </div>
          <div style={{ flex: 1, textAlign: 'right', fontSize: 11 }}>Declaro que estou de acordo<br />com as especificações acima.</div>
        </div>
      </div>
      <div style={S.footerSite}>rematei.com.br</div>
    </div>
  )
}

export default function MapaCompradorV2Preview({ dados, showDbVendedor, colunasAtivas = DEFAULT_COLUNAS_V2_COMP, destaqueTotalCompradores = 'liquido', ocultarTotalLiquido = false, orgId, leilaoId, compradorId, tipo, onReload, ordenacao = 'numero_lote' }) {
  const iframeRef = useRef(null)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [showPdfModal, setShowPdfModal] = useState(false)
  const [showAlterarComissao, setShowAlterarComissao] = useState(false)

  const [reciboPdfUrl, setReciboPdfUrl] = useState(null)
  const [showReciboModal, setShowReciboModal] = useState(false)
  const [reciboTitle, setReciboTitle] = useState('')
  const reciboIframeRef = useRef(null)
  const [showGtaModal, setShowGtaModal] = useState(false)

  if (!dados) return null
  const { leilao, organizador, compradores } = dados

  const fluxo = leilao?.fluxo_comissao || ''
  const mostraBotaoGta = fluxo === 'gta_nota' && compradores.length === 1 && compradorId
  const compGta  = compradores.length === 1 ? !!compradores[0]?.gta_isento  : false
  const compNota = compradores.length === 1 ? !!compradores[0]?.nota_isento : false

  const { gtaNoModal, notaNoModal } = (() => {
    if (!mostraBotaoGta || !compradores[0]) return { gtaNoModal: 0, notaNoModal: 0 }
    const comp = compradores[0]
    const venda = (comp.lotes || []).filter(l => l.situacao === 'VENDA')
    const valorPorAnimal = parseFloat(leilao.valor_por_animal) || 0
    const valorNota = parseFloat(leilao.valor_nota) || 0
    const gtaModo = leilao.gta_modo_cobranca || 'por_vendedor'
    const lotesGta = venda.filter(l => !l.gta_isento)
    const lotesNota = venda.filter(l => !l.nota_isento)
    const gta = valorPorAnimal * lotesGta.reduce((s, l) => s + (l.qtd || 0), 0)
    const nota = gtaModo === 'por_lote'
      ? valorNota * lotesNota.length
      : valorNota * new Set(lotesNota.filter(l => l.vendedor_id).map(l => l.vendedor_id)).size
    return { gtaNoModal: gta, notaNoModal: nota }
  })()

  const handleReciboComissao = async () => {
    try {
      const resp = await api.get(
        `/organizadores/${orgId}/recibos/mapa-comprador/${leilaoId}/${compradores[0].id}/recibo-comissao`,
        { responseType: 'blob', timeout: 30000 }
      )
      const blob = new Blob([resp.data], { type: 'application/pdf' })
      if (reciboPdfUrl) URL.revokeObjectURL(reciboPdfUrl)
      const url = URL.createObjectURL(blob)
      setReciboTitle(`Recibo Comissão - ${compradores[0].nome}`)
      openPdf(url, setReciboPdfUrl, setShowReciboModal)
    } catch (err) {
      import('react-hot-toast').then(m => m.default.error('Erro ao gerar recibo de comissão'))
    }
  }

  const handleReciboRepasse = async () => {
    try {
      const resp = await api.get(
        `/organizadores/${orgId}/recibos/mapa-comprador/${leilaoId}/${compradores[0].id}/recibo-repasse-todos`,
        { responseType: 'blob', timeout: 60000 }
      )
      const blob = new Blob([resp.data], { type: 'application/pdf' })
      if (reciboPdfUrl) URL.revokeObjectURL(reciboPdfUrl)
      const url = URL.createObjectURL(blob)
      setReciboTitle(`Recibo Repasse - ${compradores[0].nome}`)
      openPdf(url, setReciboPdfUrl, setShowReciboModal)
    } catch (err) {
      import('react-hot-toast').then(m => m.default.error('Erro ao gerar recibo de repasse'))
    }
  }

  const closeReciboModal = () => {
    setShowReciboModal(false)
    if (reciboPdfUrl) { URL.revokeObjectURL(reciboPdfUrl); setReciboPdfUrl(null) }
  }

  const printRecibo = () => {
    if (reciboIframeRef.current?.contentWindow) {
      reciboIframeRef.current.contentWindow.focus()
      reciboIframeRef.current.contentWindow.print()
    }
  }

  const handleImprimir = async () => {
    setLoadingPdf(true)
    try {
      const qs = new URLSearchParams()
      if (compradorId) qs.set('comprador_id', compradorId)
      qs.set('tipo', tipo || 'padrao')
      qs.set('dados_bancarios_vendedor', String(showDbVendedor))
      qs.set('versao', '2')
      qs.set('ordenacao', ordenacao)
      qs.set('mostrar_curral', String(colunasAtivas.includes('curral')))
      qs.set('mostrar_pct', String(colunasAtivas.includes('pct')))
      const resp = await api.get(
        `/organizadores/${orgId}/mapas/leilao/${leilaoId}/mapa-comprador?${qs}`,
        { responseType: 'blob', timeout: 60000 }
      )
      const blob = new Blob([resp.data], { type: 'application/pdf' })
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      openPdf(URL.createObjectURL(blob), setPdfUrl, setShowPdfModal)
    } catch (err) {
      alert('Erro ao gerar PDF: ' + (err.response?.statusText || err.message))
    } finally { setLoadingPdf(false) }
  }

  const closePdfModal = () => { setShowPdfModal(false); if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) } }
  const printPdf = () => { if (iframeRef.current?.contentWindow) { iframeRef.current.contentWindow.focus(); iframeRef.current.contentWindow.print() } }
  const pdfFilename = `Mapa do Comprador V2 - ${compradores.length === 1 ? compradores[0].nome : `Todos (${compradores.length})`}.pdf`.replace(/[/\\?%*:|"<>]/g, '-')

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
        <p className="text-sm text-gray-600 flex-1 min-w-0 truncate">
          <span className="font-medium text-gray-800">{leilao.nome}</span>
          {' · '}{compradores.length === 1 ? compradores[0].nome : `${compradores.length} compradores`}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={handleImprimir} disabled={loadingPdf}
            className="flex items-center gap-1.5 px-4 py-2.5 sm:px-3 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm sm:text-xs font-semibold rounded-lg transition-colors disabled:opacity-60">
            <Printer className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
            <span className="hidden sm:inline">{loadingPdf ? 'Gerando...' : 'Imprimir'}</span>
            <span className="sm:hidden">{loadingPdf ? 'Gerando...' : 'PDF'}</span>
          </button>
          {compradores.length === 1 && compradorId && (
            <PrazoComissao orgId={orgId} leilaoId={leilaoId} fluxo={fluxo} comprador={compradores[0]} onChanged={onReload} />
          )}
          {compradores.length === 1 && (
            <>
              <button onClick={handleReciboComissao}
                className="flex items-center gap-1 px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-medium rounded-lg transition-colors"
                title="Recibo de recebimento de comissões + despesas">
                <span className="hidden sm:inline">Recibo </span>Com.
              </button>
              <button onClick={handleReciboRepasse}
                className="flex items-center gap-1 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium rounded-lg transition-colors"
                title="Recibo de recebimento do valor a repassar para vendedor">
                <span className="hidden sm:inline">Recibo </span>Repasse
              </button>
              <button onClick={() => setShowAlterarComissao(true)}
                className="hidden sm:flex items-center gap-1 px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-lg transition-colors"
                title="Alterar comissão de compra dos lotes">
                Alterar Com.
              </button>
              {mostraBotaoGta && (() => {
                const algum = compGta || compNota
                const txt = compGta && compNota ? 'GTA/Nota isentos'
                  : compGta ? 'GTA isento'
                  : compNota ? 'Nota isenta'
                  : 'Isentar GTA/Nota'
                return (
                  <button
                    onClick={() => setShowGtaModal(true)}
                    className={`flex items-center gap-1 px-3 py-2 text-white text-xs font-medium rounded-lg transition-colors ${algum ? 'bg-blue-500 hover:bg-blue-600' : 'bg-teal-600 hover:bg-teal-700'}`}
                    title="Isentar/reverter GTA e Nota do comprador"
                  >
                    {txt}
                  </button>
                )
              })()}
            </>
          )}
        </div>
      </div>

      {/* ── Aviso mobile (oculto em sm+) ── */}
      <div className="sm:hidden bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
        <svg className="w-8 h-8 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p>O preview é otimizado para desktop.</p>
        <p className="mt-1">Use os botões acima para gerar o PDF.</p>
      </div>

      <div className="hidden sm:block" style={{ background: '#f3f4f6', padding: '24px 0' }}>
        {compradores.map((comp, i) => (
          <div key={comp.id || i} style={{ maxWidth: 960, margin: '0 auto 24px', background: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.10)', borderRadius: 8, overflow: 'hidden' }}>
            <MapaPageV2 comp={comp} leilao={leilao} organizador={organizador} showDbVendedor={showDbVendedor} colunasAtivas={colunasAtivas} destaqueTotalCompradores={destaqueTotalCompradores} ocultarTotalLiquido={ocultarTotalLiquido} />
          </div>
        ))}
      </div>

      {showPdfModal && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 sm:p-4">
          <div className="bg-white w-full max-w-5xl flex flex-col rounded-t-2xl sm:rounded-xl shadow-2xl" style={{ height: '92dvh' }}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl sm:rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800 text-sm truncate flex-1 min-w-0">Mapa do Comprador V2 — {leilao.nome}</span>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button onClick={() => downloadPdf(pdfUrl, pdfFilename)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Salvar PDF
                </button>
                <button onClick={printPdf} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                  <Printer className="w-4 h-4" /> Imprimir
                </button>
                <button onClick={closePdfModal} className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">Fechar</button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl">
              <iframe ref={iframeRef} src={pdfUrl} title={pdfFilename} style={{ width: '100%', height: '100%', border: 'none' }} />
            </div>
          </div>
        </div>
      )}

      {/* Modal Recibo PDF */}
      {showReciboModal && reciboPdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 sm:p-4">
          <div className="bg-white w-full max-w-5xl flex flex-col rounded-t-2xl sm:rounded-xl shadow-2xl" style={{ height: '92dvh' }}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl sm:rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800 text-sm truncate flex-1 min-w-0">{reciboTitle}</span>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button onClick={() => downloadPdf(reciboPdfUrl, `${reciboTitle}.pdf`)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors">
                  Salvar PDF
                </button>
                <button onClick={printRecibo} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                  <Printer className="w-4 h-4" /> Imprimir
                </button>
                <button onClick={closeReciboModal} className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">Fechar</button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl">
              <iframe ref={reciboIframeRef} src={reciboPdfUrl} title={reciboTitle} style={{ width: '100%', height: '100%', border: 'none' }} />
            </div>
          </div>
        </div>
      )}

      {/* Modal Isentar/Reverter GTA */}
      {showGtaModal && compradores.length === 1 && (
        <IsentarGTAModal
          compradorNome={compradores[0].nome}
          compradorId={compradorId}
          leilaoId={leilaoId}
          orgId={orgId}
          gtaIsento={compGta}
          notaIsento={compNota}
          totalGta={gtaNoModal}
          totalNota={notaNoModal}
          totalGtaNota={gtaNoModal + notaNoModal}
          onClose={() => setShowGtaModal(false)}
          onSaved={() => { setShowGtaModal(false); if (onReload) onReload() }}
        />
      )}

      {/* Modal Alterar Comissão */}
      {showAlterarComissao && compradores.length === 1 && (
        <AlterarComissaoModal
          tipo="compra"
          lotes={compradores[0].lotes.filter(l => l.situacao !== 'DEFESA')}
          organizadorId={orgId}
          onClose={() => setShowAlterarComissao(false)}
          onSaved={() => { if (onReload) onReload() }}
        />
      )}
    </>
  )
}
